import * as React from 'react';
import {generatePath} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {ButtonGroup} from 'shells/button-group/button-group';
import {PageControlBar} from 'shells/control-bar/page-control-bar';
import {StatusBarSecondaryTimestamp} from 'shells/control-bar/status-bar-secondary-timestamp';
import {ErrorState} from 'shells/error-state/error-state';
import {CursorRegularIcon} from 'shells/icon/svg/cursor-regular-icon';
import {ViewExploreIcon} from 'shells/icon/svg/view-explore-icon';
import {Stack} from 'shells/layout/stack/stack';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {Switch} from 'shells/switch/switch';
import {MARKETING_AUTOMATIONS_TOUR_ACTIVITY_LOG_ID} from 'shells/tours/tours';
import {Body} from 'shells/typography/body';

import {appHistory} from '../../../history';

import {PageContainer, PageTitleBar} from '../../layout';
import {useGetTrackingSnippetStatus} from '../../setup/tracking-snippet/graphql/hooks';

import {Automation} from './automation';
import {useGetMoneyPage} from './graphql/hooks/use-get-money-page';
import {useSetMoneyPageIsEnabled} from './graphql/hooks/use-set-money-page-is-enabled';
import {useUpdateMoneyPage} from './graphql/hooks/use-update-money-page';
import {PageActivity} from './page-activity';
import {SnippetStatusBanner} from './snippet-status-banner';
import {TestPageModal} from './test-page-modal';

const AUTOMATION_TAB_ID = 'automation';
export const PAGE_ACTIVITY_TAB_ID = 'page-activity';

type Props = {
    id: string;
    match: {
        params: {
            moneyPageId: string;
            tab: string;
        };
        path: string;
    };
};

export function MoneyPage(props: Props) {
    const [isTestPageModalOpen, setIsTestPageModalOpen] = React.useState(false);
    const activeTab = props.match.params.tab || AUTOMATION_TAB_ID;

    const {moneyPage, isLoading, error: errorMessage} = useGetMoneyPage(props.id);
    const {trackingSnippetStatus, isLoading: isLoadingTrackingSnippetStatus} =
        useGetTrackingSnippetStatus();
    const {updateMoneyPage} = useUpdateMoneyPage();
    const {setMoneyPageIsEnabled} = useSetMoneyPageIsEnabled();

    if (errorMessage) {
        return <ErrorState errorMessage={errorMessage} />;
    }

    if (isLoading || !moneyPage) {
        return <LoadingPage />;
    }

    const tabOptions = [
        {
            id: AUTOMATION_TAB_ID,
            label: 'Automation',
            icon: CursorRegularIcon,
            component: <Automation moneyPage={moneyPage} />,
        },
        {
            id: PAGE_ACTIVITY_TAB_ID,
            label: 'Page activity',
            icon: ViewExploreIcon,
            component: <PageActivity moneyPageId={props.id} />,
        },
    ];

    const onChangeTab = (newTab: string) => {
        const newPath = generatePath(props.match.path, {
            moneyPageId: props.id,
            tab: newTab,
        });

        appHistory.push(newPath);
    };

    return (
        <PageContainer
            title='Marketing automation'
            pageTitleBar={
                <PageTitleBar
                    title={moneyPage.name}
                    editInPlaceProps={{
                        onChange: (newValue: string | null | undefined) => {
                            return updateMoneyPage({
                                id: moneyPage.id,
                                name: newValue ? newValue : '',
                            });
                        },
                        placeholder: 'Name this automation',
                    }}
                    subheaderPrimaryText={null}
                    breadcrumbs={[
                        {
                            to: '/marketing/automations',
                            text: 'Marketing automations',
                        },
                    ]}
                    buttons={
                        <ButtonGroup spacing={16}>
                            <Stack isHorizontal={true} spacing={8}>
                                <Switch
                                    onChange={() => {
                                        setMoneyPageIsEnabled({
                                            id: moneyPage.id,
                                            isEnabled: !moneyPage.isEnabled,
                                        });
                                    }}
                                    isRounded={true}
                                    isOn={Boolean(moneyPage.isEnabled)}
                                    isSmall={true}
                                />
                                <Body size='small'>Enable automation</Body>
                            </Stack>
                            <Button
                                variant='primary'
                                size='big'
                                onClick={() => {
                                    setIsTestPageModalOpen(true);
                                }}
                            >
                                Test automation
                            </Button>
                        </ButtonGroup>
                    }
                />
            }
            controlBar={
                <div id={MARKETING_AUTOMATIONS_TOUR_ACTIVITY_LOG_ID}>
                    <PageControlBar
                        statusInfo={{
                            primaryText: `${moneyPage.views} ${
                                moneyPage.views === 1 ? 'identified visitor' : 'identified visitors'
                            }`,
                            secondaryText: (
                                <StatusBarSecondaryTimestamp
                                    datetime={moneyPage.lastTriggeredTime}
                                    prefix='Last visit'
                                    emptyText='No visits yet'
                                    displayConfig={{
                                        type: 'relative',
                                    }}
                                />
                            ),
                        }}
                        tabs={{
                            selectedTab: activeTab,
                            options: tabOptions,
                            onChangeTab: onChangeTab,
                        }}
                    />
                </div>
            }
            noPadding={true}
            whiteBackground={true}
            flashBanner={
                !isLoadingTrackingSnippetStatus &&
                trackingSnippetStatus &&
                (trackingSnippetStatus.status === 'NEVER_CONFIGURED' ||
                    trackingSnippetStatus.status === 'NOT_WORKING') ? (
                    <SnippetStatusBanner status={trackingSnippetStatus.status} />
                ) : undefined
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={activeTab} tabOptions={tabOptions} />
            <TestPageModal
                isOpen={isTestPageModalOpen}
                onClose={() => setIsTestPageModalOpen(false)}
                automation={moneyPage}
            />
        </PageContainer>
    );
}
